import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";
import { useSelector } from "react-redux";

import { getToken } from "../Utils/helper";
import api from "../api/api";
import { IDLE_TIMEOUT_MESSAGE } from "../auth/sessionEndMessages.mjs";
import { shouldLogoutForIdle } from "../auth/sessionTiming.mjs";
import {
  clearAccessToken,
  clearSensitiveClientState,
  getIdleDeadlineAt,
  setAuthenticationTiming,
  subscribeToAccessToken,
  subscribeToSessionTiming,
} from "../auth/tokenStore";
import { persistor } from "../Redux/store";
import { getStoredFirmLoginPath } from "../auth/firmLogin.mjs";

const InactivityContext = createContext();
const HEARTBEAT_INTERVAL_MS = 60_000;
const hasIdleDeadline = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

export const InactivityProvider = ({ children }) => {
  const configuredTimeout = useSelector((state) => state.general.inactivityTimeout);
  const minutes = Math.min(Math.max(parseInt(configuredTimeout, 10) || 10, 5), 30);
  const [authenticated, setAuthenticated] = useState(Boolean(getToken()));
  const authenticatedRef = useRef(authenticated);
  const [modalState, setModalState] = useState({
    show: false,
    timeoutTime: null,
  });
  const lastHeartbeatRef = useRef(0);
  const deadlineRef = useRef(getIdleDeadlineAt());
  const activityChannelRef = useRef(null);
  const logoutInProgressRef = useRef(false);

  const terminateLocalSession = useCallback(async () => {
    sessionStorage.setItem("auth_end_message", IDLE_TIMEOUT_MESSAGE);
    clearAccessToken({ reason: "idle_timeout" });
    persistor.pause();
    await persistor.flush();
    await persistor.purge();
    clearSensitiveClientState();
    // The logged-out route may briefly mount while cleanup is in progress and
    // consume this one-shot value, so restore it immediately before navigation.
    sessionStorage.setItem("auth_end_message", IDLE_TIMEOUT_MESSAGE);
    window.location.replace(getStoredFirmLoginPath());
  }, []);

  const handleLogout = useCallback(async () => {
    if (logoutInProgressRef.current) return;
    logoutInProgressRef.current = true;
    sessionStorage.setItem("auth_end_message", IDLE_TIMEOUT_MESSAGE);
    try {
      await api.post("/api/logout/");
    } finally {
      await terminateLocalSession();
    }
  }, [terminateLocalSession]);

  const onIdle = useCallback(async () => {
    if (!shouldLogoutForIdle(Boolean(getToken()), deadlineRef.current)) return;
    await handleLogout();
  }, [handleLogout]);

  const onIdleWarning = useCallback(() => {
    if (!hasIdleDeadline(deadlineRef.current)) return;
    const deadline = Number(deadlineRef.current);
    const serverRemaining = deadline - Date.now();
    if (getToken() && serverRemaining <= 60_000) {
      setModalState((previous) => ({ ...previous, show: true }));
    }
  }, []);

  const sendHeartbeat = useCallback(async () => {
    if (!getToken() || document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastHeartbeatRef.current < HEARTBEAT_INTERVAL_MS) return;
    lastHeartbeatRef.current = now;
    try {
      const response = await api.post("/api/auth/activity/");
      setAuthenticationTiming({
        sessionExpiresIn: response.data.session_expires_in,
        idleExpiresIn: response.data.idle_expires_in,
        idleFallbackSeconds: minutes * 60,
      });
    } catch {
      // Authentication errors are handled centrally by the API interceptor.
    }
  }, [minutes]);

  const onAction = useCallback(
    (event) => {
      if (!event?.isTrusted || document.visibilityState !== "visible") return;
      const activity = { type: "activity", at: Date.now() };
      if (activityChannelRef.current) {
        activityChannelRef.current.postMessage(activity);
      } else {
        localStorage.setItem("bp-user-activity-event", String(activity.at));
        localStorage.removeItem("bp-user-activity-event");
      }
      sendHeartbeat();
    },
    [sendHeartbeat]
  );

  const idleTimerConfig = useMemo(
    () => ({
      onIdle,
      onAction,
      timeout: minutes * 60 * 1000,
      throttle: 500,
      onPrompt: onIdleWarning,
      promptBeforeIdle: 60 * 1000,
      crossTab: false,
      disabled: !authenticated,
      events: ["pointerdown", "pointermove", "keydown", "touchstart", "scroll", "visibilitychange"],
    }),
    [authenticated, minutes, onAction, onIdle, onIdleWarning]
  );

  const { getRemainingTime, reset } = useIdleTimer(idleTimerConfig);

  useEffect(() => {
    const handleTokenChange = (token) => {
      const nextAuthenticated = Boolean(token);
      const authenticationChanged = authenticatedRef.current !== nextAuthenticated;
      authenticatedRef.current = nextAuthenticated;
      setAuthenticated(nextAuthenticated);

      // Access-token rotation keeps the same authenticated session alive. Do
      // not reset inactivity state as though the user had just logged in.
      if (!authenticationChanged) return;

      logoutInProgressRef.current = false;
      lastHeartbeatRef.current = 0;
      if (!nextAuthenticated) {
        deadlineRef.current = null;
        setModalState({ show: false, timeoutTime: null });
        return;
      }
      deadlineRef.current = getIdleDeadlineAt();
      reset();
    };
    const handleTimingChange = ({ idleDeadlineAt }) => {
      deadlineRef.current = idleDeadlineAt;
      if (Number.isFinite(Number(idleDeadlineAt)) && Number(idleDeadlineAt) - Date.now() > 60_000) {
        setModalState((previous) => ({ ...previous, show: false, timeoutTime: null }));
      }
    };

    handleTokenChange(getToken());
    handleTimingChange({ idleDeadlineAt: getIdleDeadlineAt() });
    const unsubscribeToken = subscribeToAccessToken(handleTokenChange);
    const unsubscribeTiming = subscribeToSessionTiming(handleTimingChange);
    return () => {
      unsubscribeToken();
      unsubscribeTiming();
    };
  }, [reset]);

  useEffect(() => {
    const channel =
      typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("bp-user-activity") : null;
    activityChannelRef.current = channel;
    const handleCrossTabActivity = (event) => {
      if (event.data?.type === "activity") {
        reset();
      }
    };
    const handleStorageActivity = (event) => {
      if (event.key === "bp-user-activity-event" && event.newValue) {
        reset();
      }
    };
    channel?.addEventListener("message", handleCrossTabActivity);
    window.addEventListener("storage", handleStorageActivity);
    return () => {
      channel?.removeEventListener("message", handleCrossTabActivity);
      window.removeEventListener("storage", handleStorageActivity);
      channel?.close();
      activityChannelRef.current = null;
    };
  }, [reset]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!getToken() || logoutInProgressRef.current) return;
      if (!hasIdleDeadline(deadlineRef.current)) return;
      const remaining = Number(deadlineRef.current) - Date.now();
      if (shouldLogoutForIdle(true, deadlineRef.current)) {
        handleLogout();
      } else if (Number.isFinite(remaining) && remaining <= 60_000) {
        setModalState((previous) => ({ ...previous, show: true }));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [handleLogout]);

  useEffect(() => {
    if (!modalState.show) return undefined;
    const interval = setInterval(() => {
      const localRemaining = Math.ceil(getRemainingTime() / 1000);
      const serverRemaining = deadlineRef.current
        ? Math.ceil((deadlineRef.current - Date.now()) / 1000)
        : localRemaining;
      setModalState((previous) => ({
        ...previous,
        timeoutTime: Math.max(0, Math.min(localRemaining, serverRemaining)),
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, [getRemainingTime, modalState.show]);

  const hideInactivityModal = useCallback(() => {
    reset();
    setModalState({ show: false, timeoutTime: null });
    sendHeartbeat();
  }, [reset, sendHeartbeat]);

  const value = useMemo(
    () => ({
      inactivityModalShow: modalState.show,
      timeoutTime: modalState.timeoutTime,
      hideInactivityModal,
      getRemainingTime,
      reset,
    }),
    [getRemainingTime, hideInactivityModal, modalState, reset]
  );

  return <InactivityContext.Provider value={value}>{children}</InactivityContext.Provider>;
};

export const useInactivity = () => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error("useInactivity must be used within an InactivityProvider");
  }
  return context;
};
